import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Center, IconButton, Image, Text } from '@chakra-ui/react';
import { GatsbyImage, getSrc } from 'gatsby-plugin-image';
import { forwardRef } from 'react';
import { FaImage } from '@react-icons/all-files/fa/FaImage';
import { FaTrashAlt } from '@react-icons/all-files/fa/FaTrashAlt';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { connectField } from '../../connectors';
import { useMediaModal } from '../../contexts/media-modal';
import { useNotificationsContext } from '../../contexts/notifications';
import { PageProvider, usePageContext } from '../../contexts/page';
import { HighlightTooltip } from '../components/HighlightTooltip';
import { useFileObjectUrl } from '../../utils/open-storage-gateway';
import { useImage } from './hooks/use-image';
export const ImageField = connectField(({ jaenField, lightbox, lightboxGroup, defaultValue, overload, sizes, autoScale = true, intrinsicWidth, intrinsicHeight, ...imageProps }) => {
    const isLightbox = lightbox && !jaenField.isEditing;
    let mediaId = jaenField.value;
    if (mediaId === undefined) {
        mediaId = jaenField.staticValue;
    }
    const { jaenPage } = usePageContext();
    const context = useMediaModal({
        id: jaenField.id || jaenField.name,
        jaenPageId: jaenPage.id,
        onSelect: media => {
            jaenField.onUpdateValue(media.id);
        }
    });
    const { confirm } = useNotificationsContext();
    const handleRemove = async () => {
        const ok = await confirm({
            title: 'Remove Image',
            message: 'Are you sure you want to remove this image?'
        });
        if (ok) {
            jaenField.onUpdateValue(null);
        }
    };
    return (_jsx(PageProvider, { jaenPage: {
            id: 'JaenPage /cms/media/',
            mediaNodes: jaenPage.mediaNodes || []
        }, children: _jsx(HighlightTooltip, { id: jaenField.id || jaenField.name, author: jaenField.lastAuthor, isEditing: jaenField.isEditing, boxSize: autoScale ? 'full' : 'fit-content', actions: [
                _jsxs(Button, { variant: 'field-highlighter-tooltip', onClick: () => {
                        context.toggleModal({ defaultSelected: mediaId });
                    }, children: [_jsx(FaImage, {}), "Image"] }),
                _jsx(IconButton, { variant: "field-highlighter-tooltip", "aria-label": "Remove", onClick: handleRemove, children: _jsx(FaTrashAlt, {}) })
            ], as: ImageComponent, asProps: {
                mediaId,
                fieldName: jaenField.name,
                imageProps,
                lightbox: isLightbox,
                lightboxGroup,
                defaultValue,
                sizes,
                autoScale,
                intrinsicWidth,
                intrinsicHeight
            } }) }));
}, {
    fieldType: 'IMA:ImageField'
});
const ImageComponent = forwardRef(({ mediaId, fieldName, imageProps = {}, lightbox, lightboxGroup, defaultValue, sizes, autoScale, intrinsicWidth, intrinsicHeight, ...props }, ref) => {
    const image = useImage(mediaId || '');
    const defaultSrc = useFileObjectUrl(defaultValue);
    if (!image && !defaultValue) {
        return (_jsx(Center, { ref: ref, boxSize: autoScale ? 'full' : undefined, pos: 'relative', overflow: "hidden", style: imageProps?.style, className: imageProps?.className, ...props, children: _jsx(Text, { color: "gray.600", fontSize: "sm", children: "No image" }) }));
    }
    let element = (_jsx(Box, { ref: ref, boxSize: autoScale ? 'full' : undefined, pos: 'relative', overflow: "hidden", cursor: lightbox ? 'zoom-in' : 'default', ...props, children: image ? (_jsx(GatsbyImage, { image: image?.image, alt: image.description, ...imageProps, sizes: sizes, style: {
                ...imageProps?.style,
                ...(autoScale ? { height: '100%', width: '100%' } : {})
            } })) : (_jsx(Image, { ...imageProps, sizes: sizes, src: defaultSrc, boxSize: autoScale ? 'full' : undefined, style: imageProps?.style, asChild: intrinsicWidth && intrinsicHeight ? true : undefined, children: intrinsicWidth && intrinsicHeight ? (_jsx("img", { src: defaultSrc, width: intrinsicWidth, height: intrinsicHeight, alt: imageProps?.alt, loading: "lazy", decoding: "async" })) : undefined })) }));
    if (lightbox) {
        const src = image ? getSrc(image.image) : defaultSrc;
        element = _jsx(PhotoView, { src: src, children: element });
        if (!lightboxGroup) {
            element = (_jsx(PhotoProvider, { maskOpacity: 0.8, children: _jsx(PhotoView, { src: src, children: element }) }));
        }
    }
    return element;
});
